import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { 
  FileText, 
  Plus, 
  Calendar, 
  Weight, 
  TrendingUp,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getStats, getAllDDR, initializeDB } from "@/services/localDatabase";
import { downloadDDRPdf } from "@/services/pdfGenerator";
import { toast } from "sonner";

const Dashboard = () => {
  const [stats, setStats] = useState({ total_ddr: 0, this_month_ddr: 0, total_weight: 0 });
  const [recentDDRs, setRecentDDRs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      await initializeDB();
      const [statsData, ddrsData] = await Promise.all([
        getStats(),
        getAllDDR()
      ]);
      setStats(statsData);
      setRecentDDRs(ddrsData.slice(0, 5));
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (ddr) => {
    try {
      await downloadDDRPdf(ddr);
      toast.success("PDF scaricato");
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error("Errore nel download");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    // Handle both ISO format and DD/MM/YYYY format
    if (dateString.includes('/')) return dateString;
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT');
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">Gestione Documenti di Registrazione</p>
        </div>
        <Link to="/nuovo">
          <Button 
            data-testid="new-ddr-btn"
            className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-11 px-8"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nuovo DDR
          </Button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="stat-card border-slate-100" data-testid="stat-total">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                  Totale DDR
                </p>
                <p className="text-3xl font-bold text-slate-900 font-barlow mt-1">
                  {loading ? "-" : stats.total_ddr}
                </p>
              </div>
              <div className="w-12 h-12 bg-slate-900 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-slate-100" data-testid="stat-month">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                  Questo Mese
                </p>
                <p className="text-3xl font-bold text-slate-900 font-barlow mt-1">
                  {loading ? "-" : stats.this_month_ddr}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                <Calendar className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card border-slate-100" data-testid="stat-weight">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
                  Peso Totale
                </p>
                <p className="text-3xl font-bold text-slate-900 font-barlow mt-1">
                  {loading ? "-" : `${(stats.total_weight || 0).toLocaleString('it-IT')} kg`}
                </p>
              </div>
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                <Weight className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent DDRs */}
      <Card className="border-slate-100" data-testid="recent-ddrs">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <CardTitle className="font-barlow text-lg font-semibold text-slate-900">
              DDR Recenti
            </CardTitle>
            <Link to="/storico">
              <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                <TrendingUp className="w-4 h-4 mr-2" />
                Vedi tutti
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : recentDDRs.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun DDR creato</p>
              <Link to="/nuovo">
                <Button className="mt-4 bg-orange-500 hover:bg-orange-600">
                  Crea il primo DDR
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>N° DDR</th>
                    <th>Data</th>
                    <th>Specie</th>
                    <th>Destinatario</th>
                    <th>Peso</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {recentDDRs.map((ddr) => (
                    <tr key={ddr.id} data-testid={`ddr-row-${ddr.id}`}>
                      <td>
                        <Badge variant="outline" className="font-mono">
                          {ddr.serie}-{ddr.numero_ddr}
                        </Badge>
                      </td>
                      <td>{formatDate(ddr.data_documento)}</td>
                      <td className="max-w-[200px] truncate">{ddr.specie}</td>
                      <td>{ddr.destinazione?.nome || "-"}</td>
                      <td className="font-medium">{ddr.peso_kg} kg</td>
                      <td>
                        <Button
                          data-testid={`download-btn-${ddr.id}`}
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownload(ddr)}
                          className="text-slate-600 hover:text-orange-500"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
